import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Check, UserRound, X } from 'lucide-react';
import { useState } from 'react';

export interface PersonOption {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    /** Optional avatar URL. Falls back to initials when absent. */
    avatar?: string | null;
    /** Optional role shown as a badge — useful when the list mixes roles. */
    role?: string | null;
}

interface PersonSelectBase {
    /**
     * The candidate list. The caller decides who is eligible — any role or none — so
     * this component is not tied to a single role.
     */
    people: PersonOption[];
    /** Ids surfaced under a "Suggested" heading at the top (e.g. the current user, a team). */
    suggestedIds?: string[];
    /** Show the role badge next to each name. Off by default. */
    showRole?: boolean;
    disabled?: boolean;
    className?: string;
    /**
     * 'chip'   — compact avatar affordance, sized to its content.
     * 'field'  — fills the width of a form row, always bordered.
     * 'inline' — full width, but drops the border and background once a selection
     *            exists so it reads as a value rather than an input. Keeps the
     *            chrome while empty so it is still discoverable.
     */
    variant?: 'chip' | 'field' | 'inline';
    /**
     * Set when this sits inside a Dialog, Sheet or Drawer. Those make the rest of the
     * page inert, which otherwise swallows clicks on the list — it is portalled out to
     * the same inert body.
     */
    modal?: boolean;
    /**
     * Copy overrides. Each falls back to a generic `common.person_select.*` string, so
     * a domain can say "Search engineers…" without the component knowing about engineers.
     */
    labels?: {
        placeholder?: string;
        search?: string;
        empty?: string;
        suggestedHeading?: string;
        peopleHeading?: string;
        clear?: string;
        unassignedOption?: string;
        /** Multi mode only: shown as "3 {selected}" once more than `maxAvatars` are picked. */
        selected?: string;
    };
}

export interface PersonSelectSingleProps extends PersonSelectBase {
    multiple?: false;
    value: string | null;
    onValueChange: (value: string | null) => void;
    /** Render the inline × that clears the selection. */
    allowClear?: boolean;
    /**
     * Offer an explicit "Unassigned" entry at the top of the list. Preferred over the ×
     * where the trigger has no room for it, e.g. the inline variant.
     */
    showUnassignedOption?: boolean;
}

export interface PersonSelectMultipleProps extends PersonSelectBase {
    multiple: true;
    values: string[];
    onValuesChange: (values: string[]) => void;
    /** How many avatars to stack on the trigger before collapsing to a count. */
    maxAvatars?: number;
}

export type PersonSelectProps = PersonSelectSingleProps | PersonSelectMultipleProps;

function fullName(person: PersonOption | null | undefined): string {
    if (!person) {
        return '';
    }

    return [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
}

function initials(person: PersonOption): string {
    const first = person.first_name?.[0] ?? '';
    const last = person.last_name?.[0] ?? '';

    return (first + last).toUpperCase() || '?';
}

/**
 * Asana-style person picker: a round avatar that reads as "unassigned" until someone
 * is picked, opening a searchable list of people with avatars.
 *
 * Deliberately controlled and prop-fed rather than fetching its own list — the caller
 * owns eligibility (whatever roles it wants), and the server is expected to validate the
 * same set. Use this wherever a single person is assigned: engineer, reviewer, owner, …
 */
export function PersonSelect(props: PersonSelectProps) {
    const { people, suggestedIds = [], showRole = false, disabled = false, className, variant = 'chip', modal = false, labels } = props;
    const isMultiple = props.multiple === true;

    const { t } = useTranslations();
    const [open, setOpen] = useState(false);

    const placeholder = labels?.placeholder ?? t('common.person_select.unassigned', 'Unassigned');
    const searchLabel = labels?.search ?? t('common.person_select.search', 'Search people…');
    const emptyLabel = labels?.empty ?? t('common.person_select.empty', 'No people found.');
    const suggestedHeading = labels?.suggestedHeading ?? t('common.person_select.suggested', 'Suggested');
    const peopleHeading = labels?.peopleHeading ?? t('common.person_select.people', 'People');
    const clearLabel = labels?.clear ?? t('common.person_select.clear', 'Clear selection');
    const unassignedOptionLabel = labels?.unassignedOption ?? placeholder;
    const selectedWord = labels?.selected ?? t('common.person_select.selected', 'selected');

    // Selection state, normalised so the render path below is mode-agnostic.
    const singleValue = isMultiple ? null : (props as PersonSelectSingleProps).value;
    const multiValues = isMultiple ? (props as PersonSelectMultipleProps).values : [];
    const selectedIds = isMultiple ? multiValues : singleValue ? [singleValue] : [];

    const selected = singleValue ? (people.find((person) => person.id === singleValue) ?? null) : null;
    const selectedPeople = isMultiple ? people.filter((person) => multiValues.includes(person.id)) : [];
    const hasSelection = selectedIds.length > 0;

    const suggested = people.filter((person) => suggestedIds.includes(person.id));
    const rest = people.filter((person) => !suggestedIds.includes(person.id));

    const select = (id: string) => {
        if (isMultiple) {
            const { onValuesChange } = props as PersonSelectMultipleProps;
            // Stay open: picking several people in a row is the point of multi mode.
            onValuesChange(multiValues.includes(id) ? multiValues.filter((current) => current !== id) : [...multiValues, id]);

            return;
        }

        const { onValueChange } = props as PersonSelectSingleProps;
        onValueChange(id === singleValue ? null : id);
        setOpen(false);
    };

    const renderItem = (person: PersonOption) => (
        <CommandItem key={person.id} value={`${fullName(person)} ${person.id}`} onSelect={() => select(person.id)} className="gap-2">
            <Avatar className="h-6 w-6 shrink-0">
                <AvatarImage src={person.avatar ?? undefined} alt={fullName(person)} />
                <AvatarFallback className="text-[10px]">{initials(person)}</AvatarFallback>
            </Avatar>
            <span className="min-w-0 flex-1 truncate text-sm">{fullName(person)}</span>
            {showRole && person.role ? (
                <Badge variant="secondary" className="shrink-0 text-xs">
                    {person.role}
                </Badge>
            ) : null}
            {selectedIds.includes(person.id) ? <Check className="text-primary h-4 w-4 shrink-0" /> : null}
        </CommandItem>
    );

    const allowClear = isMultiple ? false : ((props as PersonSelectSingleProps).allowClear ?? true);
    const showUnassignedOption = isMultiple ? false : ((props as PersonSelectSingleProps).showUnassignedOption ?? false);
    const maxAvatars = isMultiple ? ((props as PersonSelectMultipleProps).maxAvatars ?? 3) : 0;

    /** Single mode only — both call sites are already gated on !isMultiple. */
    const clearSingle = () => {
        if (!isMultiple) {
            (props as PersonSelectSingleProps).onValueChange(null);
        }
    };

    const showClear = allowClear && selected !== null && !disabled;
    const isFullWidth = variant === 'field' || variant === 'inline';
    // Inline drops its chrome once filled so the value reads as text, not an input.
    const chromeless = variant === 'inline' && hasSelection;

    return (
        // Relative so the clear affordance can sit inside the field's right edge (Asana
        // style) rather than beside it. A button can't nest in a button, so the clear
        // control is an absolutely-positioned sibling overlaying the trigger.
        <div className={cn('relative flex items-center', isFullWidth && 'w-full', className)}>
            <Popover modal={modal} open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant={chromeless ? 'ghost' : 'outline'}
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn(
                            'h-auto justify-start gap-2 py-1.5 pl-1.5 font-normal',
                            isFullWidth ? 'w-full' : 'w-auto',
                            // Leave room for the clear control so the label never runs under it.
                            showClear && 'pr-9',
                            !hasSelection && 'text-muted-foreground',
                        )}
                    >
                        {!hasSelection ? (
                            <>
                                {/* Dashed ring reads as an empty seat rather than a filled avatar. */}
                                <span className="border-muted-foreground/40 text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed">
                                    <UserRound className="h-3.5 w-3.5" />
                                </span>
                                <span className="truncate text-sm">{placeholder}</span>
                            </>
                        ) : isMultiple ? (
                            <>
                                {/* Overlapping avatar stack, collapsing to a count past maxAvatars. */}
                                <span className="flex shrink-0 -space-x-2">
                                    {selectedPeople.slice(0, maxAvatars).map((person) => (
                                        <Avatar key={person.id} className="ring-background h-6 w-6 ring-2">
                                            <AvatarImage src={person.avatar ?? undefined} alt={fullName(person)} />
                                            <AvatarFallback className="text-[10px]">{initials(person)}</AvatarFallback>
                                        </Avatar>
                                    ))}
                                </span>
                                <span className="truncate text-sm">
                                    {selectedPeople.length === 1 ? fullName(selectedPeople[0]) : `${selectedPeople.length} ${selectedWord}`}
                                </span>
                            </>
                        ) : (
                            <>
                                <Avatar className="h-6 w-6 shrink-0">
                                    <AvatarImage src={selected?.avatar ?? undefined} alt={fullName(selected)} />
                                    <AvatarFallback className="text-[10px]">{selected ? initials(selected) : '?'}</AvatarFallback>
                                </Avatar>
                                <span className="truncate text-sm">{fullName(selected)}</span>
                            </>
                        )}
                    </Button>
                </PopoverTrigger>

                <PopoverContent
                    // A field-width trigger gets a field-width list; the chip variant is
                    // too narrow to match, so it keeps a readable fixed width.
                    className={cn('p-0', isFullWidth ? 'w-[var(--radix-popover-trigger-width)]' : 'w-72')}
                    align="start"
                >
                    <Command>
                        <CommandInput placeholder={searchLabel} />
                        <CommandList>
                            <CommandEmpty>{emptyLabel}</CommandEmpty>

                            {showUnassignedOption ? (
                                <CommandGroup>
                                    <CommandItem
                                        value={unassignedOptionLabel}
                                        onSelect={() => {
                                            clearSingle();
                                            setOpen(false);
                                        }}
                                        className="gap-2"
                                    >
                                        <span className="border-muted-foreground/40 text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed">
                                            <UserRound className="h-3.5 w-3.5" />
                                        </span>
                                        <span className="text-muted-foreground min-w-0 flex-1 truncate text-sm">{unassignedOptionLabel}</span>
                                        {selected === null ? <Check className="text-primary h-4 w-4 shrink-0" /> : null}
                                    </CommandItem>
                                </CommandGroup>
                            ) : null}

                            {suggested.length > 0 ? <CommandGroup heading={suggestedHeading}>{suggested.map(renderItem)}</CommandGroup> : null}

                            {/* Skipped when empty, or a caller with no candidates (a company
                                with no contacts yet) gets a bare heading and no empty state. */}
                            {rest.length > 0 ? <CommandGroup heading={peopleHeading}>{rest.map(renderItem)}</CommandGroup> : null}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {showClear ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    // stopPropagation so clearing does not also open the popover.
                    onClick={(event) => {
                        event.stopPropagation();
                        clearSingle();
                    }}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 h-6 w-6 -translate-y-1/2"
                    aria-label={clearLabel}
                >
                    <X className="h-3.5 w-3.5" />
                </Button>
            ) : null}
        </div>
    );
}
